import ko from 'knockout';
import flatpickr from 'flatpickr';
import moment, { isDuration } from 'moment';
import $ from "jquery";
import 'moment-duration-format';

import { CustomError } from './fetch';
import Inputmask from 'inputmask';
import numeral from "numeral";
import Choices from "choices.js";
import tippy from 'tippy.js';
import { formatHours } from './hours-utils';

ko.bindingHandlers.checkedRadioToBool = {
    init: function (element: any, valueAccessor: any) {
        const observable = valueAccessor();
        const interceptor = ko.computed({
            read: function () {
                return observable().toString();
            },
            write: function (newValue) {
                observable(newValue === "true");
            },
            owner: this
        });
        ko.applyBindingsToNode(element, { checked: interceptor });
    }
};

ko.bindingHandlers.singleSelectPicker = {
    init: function (element: any, valueAccessor: any, allBindingsAccessor: any) {
        let options = allBindingsAccessor().selectPickerOptions;
        let initOptions = allBindingsAccessor().initPickerOptions;
        let choices = new Choices(element, initOptions);

        choices.setChoices(options.dataset, options.valueField, options.labelField, true);

        ko.utils.registerEventHandler(element, 'addItem', (event: any) => {
            valueAccessor()(event.detail.value);

            choices.hideDropdown();
        });

        ko.utils.registerEventHandler(element, 'removeItem', () => {
            let value = choices.getValue();

            valueAccessor()(value);

            choices.hideDropdown();
        });

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            choices.destroy();
        });
    }
};

ko.bindingHandlers.selectPicker = {
    init: function (element: any, valueAccessor: any, allBindingsAccessor: any) {
        let options = allBindingsAccessor().selectPickerOptions;
        let initOptions = allBindingsAccessor().initPickerOptions;
        let choices = new Choices(element, initOptions);

        const dataset = options.dataset.map((ds: any) => {
            return {
                selected: valueAccessor()().includes(ds.id),
                ...ds
            };
        });

        choices.setChoices(dataset, options.valueField, options.labelField, true);

        ko.utils.registerEventHandler(element, 'addItem', (event: any) => {
            const selectedValueIds = valueAccessor()();
            if (!selectedValueIds.includes(event.detail.value)) {
                valueAccessor().push(event.detail.value);
            }
            choices.hideDropdown();
        });

        ko.utils.registerEventHandler(element, 'removeItem', () => {
            let values = <any[]>choices.getValue();
            let val = values.map((v) => v.value);
            valueAccessor()(val);
        });

        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
            choices.destroy();
        });

        if (ko.isObservable(valueAccessor())) {
            valueAccessor().subscribe(() => {
                const dataset = options.dataset.map((ds: any) => {
                    return {
                        selected: valueAccessor()().includes(ds.id),
                        ...ds
                    };
                });

                choices.clearStore();
                choices.setChoices(dataset, options.valueField, options.labelField, true);
            })
        }
    }
};

const defaultTimePickerOptions = {
    dateFormat: 'H:i',
    noCalendar: true,
    parseFormat: 'DD-MM-YYYY HH:mm',
    enableTime: true,
    time_24hr: true,
    allowInput: true,
    onReady: (_1: any, _2: any, instance: flatpickr.Instance) => {
        const buttonsWrapperDiv = document.createElement('div');
        buttonsWrapperDiv.style.cssText = 'display: flex; padding: 0 8px; margin: 8px 0; justify-content: space-between;';

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-clear').length === 0) {
            const resetButton = document.createElement('a');
            const resetText = document.createTextNode('Reset');
            resetButton.appendChild(resetText);

            resetButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--light');
            resetButton.addEventListener('click', () => {
                instance.clear();
                instance.close();
            });

            buttonsWrapperDiv.append(resetButton);
        }

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-ready').length === 0) {
            const readyButton = document.createElement('a');
            const readyText = document.createTextNode('Gereed');
            readyButton.appendChild(readyText);

            readyButton.classList.add('flatpickr-ready', 'clickable', 'btn', 'btn--normal', 'btn--primary');
            readyButton.addEventListener('click', () => {
                instance.close();
            });

            buttonsWrapperDiv.append(readyButton);
        }

        instance.calendarContainer.append(buttonsWrapperDiv);
    }
}

const defaultDateTimePickerOptions = {
    dateFormat: 'd-m-Y H:i',
    parseFormat: 'DD-MM-YYYY HH:mm',
    enableTime: true,
    time_24hr: true,
    allowInput: true,
    onReady: (_1: any, _2: any, instance: flatpickr.Instance) => {
        const buttonsWrapperDiv = document.createElement('div');
        buttonsWrapperDiv.style.cssText = 'display: flex; padding: 0 8px; margin: 8px 0; justify-content: space-between;';

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-clear').length === 0) {
            const resetButton = document.createElement('a');
            const resetText = document.createTextNode('Reset');
            resetButton.appendChild(resetText);

            resetButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--light');
            resetButton.addEventListener('click', () => {
                instance.clear();
                instance.close();
            });

            buttonsWrapperDiv.append(resetButton);
        }

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-ready').length === 0) {
            const readyButton = document.createElement('a');
            const readyText = document.createTextNode('Gereed');
            readyButton.appendChild(readyText);

            readyButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--primary');
            readyButton.addEventListener('click', () => {
                instance.close();
            });

            buttonsWrapperDiv.append(readyButton);
        }

        instance.calendarContainer.append(buttonsWrapperDiv);
    }
};

const defaultDateRangePickerOptions = {
    mode: 'range',
    dateFormat: 'd-m-Y H:i',
    enableTime: false,
    allowInput: false,
    onReady: (_1: any, _2: any, instance: flatpickr.Instance) => {
        const buttonsWrapperDiv = document.createElement('div');
        buttonsWrapperDiv.style.cssText = 'display: flex; padding: 0 8px; margin: 8px 0; justify-content: space-between;';

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-clear').length === 0) {
            const resetButton = document.createElement('a');
            const resetText = document.createTextNode('Reset');
            resetButton.appendChild(resetText);

            resetButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--light');
            resetButton.addEventListener('click', () => {
                instance.clear();
                instance.close();
            });

            buttonsWrapperDiv.append(resetButton);
        }

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-ready').length === 0) {
            const readyButton = document.createElement('a');
            const readyText = document.createTextNode('Gereed');
            readyButton.appendChild(readyText);

            readyButton.classList.add('flatpickr-ready', 'clickable', 'btn', 'btn--normal', 'btn--primary');
            readyButton.addEventListener('click', () => {
                instance.close();
            });

            buttonsWrapperDiv.append(readyButton);
        }

        instance.calendarContainer.append(buttonsWrapperDiv);
    }
}

const defaultDatePickerOptions = {
    dateFormat: 'd-m-Y',
    parseFormat: 'DD-MM-YYYY',
    enableTime: false,
    allowInput: true,
    onReady: (_1: any, _2: any, instance: flatpickr.Instance) => {
        const buttonsWrapperDiv = document.createElement('div');
        buttonsWrapperDiv.style.cssText = 'display: flex; padding: 0 8px; margin: 8px 0; justify-content: space-between;';

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-clear').length === 0) {
            const resetButton = document.createElement('a');
            const resetText = document.createTextNode('Reset');
            resetButton.appendChild(resetText);

            resetButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--light');
            resetButton.addEventListener('click', () => {
                instance.clear();
                instance.close();
            });

            buttonsWrapperDiv.append(resetButton);
        }

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-ready').length === 0) {
            const readyButton = document.createElement('a');
            const readyText = document.createTextNode('Gereed');
            readyButton.appendChild(readyText);

            readyButton.classList.add('flatpickr-ready', 'clickable', 'btn', 'btn--normal', 'btn--primary');
            readyButton.addEventListener('click', () => {
                instance.close();
            });

            buttonsWrapperDiv.append(readyButton);
        }

        instance.calendarContainer.append(buttonsWrapperDiv);
    }
};

const defaultMonthPickerOptions = {
    enableTime: false,
    allowInput: true,
    dateFormat: 'm-Y',
    parseFormat: 'm-Y',
    onReady: (_1: any, _2: any, instance: flatpickr.Instance) => {
        const buttonsWrapperDiv = document.createElement('div');
        buttonsWrapperDiv.style.cssText = 'display: flex; padding: 0 8px; margin: 8px 0; justify-content: space-between;';

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-clear').length === 0) {
            const resetButton = document.createElement('a');
            const resetText = document.createTextNode('Reset');
            resetButton.appendChild(resetText);

            resetButton.classList.add('flatpickr-clear', 'clickable', 'btn', 'btn--normal', 'btn--light');
            resetButton.addEventListener('click', () => {
                instance.clear();
                instance.close();
            });

            buttonsWrapperDiv.append(resetButton);
        }

        if (instance.calendarContainer.getElementsByClassName('.flatpickr-ready').length === 0) {
            const readyButton = document.createElement('a');
            const readyText = document.createTextNode('Gereed');
            readyButton.appendChild(readyText);

            readyButton.classList.add('flatpickr-ready', 'clickable', 'btn', 'btn--normal', 'btn--primary');
            readyButton.addEventListener('click', () => {
                instance.close();
            });

            buttonsWrapperDiv.append(readyButton);
        }

        instance.calendarContainer.append(buttonsWrapperDiv);
    }
};

ko.bindingHandlers.oneDecimalPercentageInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let number = numeral(element.value).value();
                valueAccessor()(number);
            } else {
                valueAccessor()(null);
            }
        });

        element.addEventListener('focusin', () => {
            if (element.createTextRange) {
                //IE fallback
                const textRange = element.createTextRange();
                textRange.move('character', 0);
                textRange.select();
            } else if (element.setSelectionRange) {
                //Browser standard
                element.setSelectionRange(0, 0);
            }
        });

        Inputmask('decimal', {
            rightAlign: false,
            autoGroup: true,
            digits: 1,
            radixPoint: ',',
            groupSeparator: '.'
        }).mask(element);
    },
    update: function (element, valueAccessor) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = numeral(value).format('0,0.0');
        element.value = text;
    }
};
ko.bindingHandlers.decimalInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let number = numeral(element.value).value();
                valueAccessor()(number);
            } else {
                valueAccessor()(null);
            }
        });

        element.addEventListener('focusin', () => {
            if (element.createTextRange) {
                //IE fallback
                const textRange = element.createTextRange();
                textRange.move('character', 0);
                textRange.select();
            } else if (element.setSelectionRange) {
                //Browser standard
                element.setSelectionRange(0, 0);
            }
        });

        Inputmask('decimal', {
            rightAlign: false,
            autoGroup: true,
            digits: 2,
            radixPoint: ',',
            groupSeparator: '.'
        }).mask(element);
    },
    update: function (element, valueAccessor) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = numeral(value).format('0,0.00');
        element.value = text;
    }
};
ko.bindingHandlers.fourDecimalInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let number = numeral(element.value).value();
                valueAccessor()(number);
            } else {
                valueAccessor()(null);
            }
        });

        element.addEventListener('focusin', () => {
            if (element.createTextRange) {
                //IE fallback
                const textRange = element.createTextRange();
                textRange.move('character', 0);
                textRange.select();
            } else if (element.setSelectionRange) {
                //Browser standard
                element.setSelectionRange(0, 0);
            }
        });

        Inputmask('decimal', {
            rightAlign: false,
            autoGroup: true,
            digits: 4,
            radixPoint: ',',
            groupSeparator: '.'
        }).mask(element);
    },
    update: function (element, valueAccessor) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = numeral(value).format('0,0.0000');
        element.value = text;
    }
};

ko.bindingHandlers.dateRangePicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDateRangePickerOptions,
            ...allBindingsAccessor().datePickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        ko.utils.registerEventHandler(element, 'focus', () => setTimeout(() => element.select(), 10));
        ko.utils.registerEventHandler(element, 'change', () => {
            let value = element.value;
            if (value) {
                valueAccessor()(value);
            }
        });
    },
    update: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDatePickerOptions,
            ...allBindingsAccessor().datePickerOptions
        };

        const picker = $(element).data('dtp');

        Object.keys(options)
            .forEach(option => picker.set(option, options[option]));

        const value = ko.unwrap(valueAccessor()) || '';
        if (!!value) {
            picker.setDate(value); //value contains string similar to `06-05-2020 tot 09-05-2020` depending on locale
        } else {
            picker.clear();
        }
    }
};

ko.bindingHandlers.datePicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDatePickerOptions,
            ...allBindingsAccessor().datePickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        ko.utils.registerEventHandler(element, 'focus', () => setTimeout(() => element.select(), 10));
        ko.utils.registerEventHandler(element, 'change', () => {
            let value: moment.Moment | undefined = moment(element.value, 'DD-MM-YYYY');
            if (!value.isValid()) {
                value = undefined;
            }
            valueAccessor()(value);
        });
    },
    update: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDatePickerOptions,
            ...allBindingsAccessor().datePickerOptions
        };

        const picker = $(element).data('dtp');

        Object.keys(options)
            .forEach(option => picker.set(option, options[option]));

        const value = ko.unwrap(valueAccessor()) || '';
        const momentValue = moment(value);
        if (momentValue.isValid()) {
            picker.setDate(momentValue.format('DD-MM-YYYY'));
        } else {
            picker.clear();
        }
    }
};

ko.bindingHandlers.dateTimePicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultDateTimePickerOptions,
            ...allBindingsAccessor().dateTimePickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        if (options.touch) {
            $((<any>picker).calendarContainer).addClass('flatpickr_touch');
        }

        ko.utils.registerEventHandler(element, 'focus', () => setTimeout(() => element.select(), 10));
        ko.utils.registerEventHandler(element, 'change', () => {
            let value: moment.Moment | undefined = moment(element.value, 'DD-MM-YYYY HH:mm');

            if (!value.isValid()) {
                value = undefined;
            }

            valueAccessor()(value);
        });
    },
    onChange: (element: any, valueAccessor: any) => {
        const picker = $(element).data('dtp');
        const value = ko.unwrap(valueAccessor()) || '';
        let momentValue = moment(value);
        console.log('momentValue (upd binding): ' + momentValue.format('YYYY-MM-DD HH:mm'));

        if (picker.config) {
            if (picker.config.minDate) {
                const minDateMoment = moment(picker.config.minDate);
                if (minDateMoment.isValid() && momentValue.isBefore(minDateMoment)) {
                    momentValue = minDateMoment;
                    valueAccessor()(momentValue);
                }
            }

            if (picker.config.maxDate) {
                const maxDateMoment = moment(picker.config.maxDate);
                if (maxDateMoment.isValid() && momentValue.isAfter(maxDateMoment)) {
                    momentValue = maxDateMoment;
                    valueAccessor()(momentValue);
                }
            }
        }

        if (momentValue.isValid()) {
            picker.setDate(momentValue.format('DD-MM-YYYY HH:mm'));
        } else {
            picker.clear();
        }
    }
};

ko.bindingHandlers.timePicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultTimePickerOptions,
            ...allBindingsAccessor().timePickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        if (options.touch) {
            $((<any>picker).calendarContainer).addClass('flatpickr_touch');
        }

        ko.utils.registerEventHandler(element, 'focus', () => setTimeout(() => element.select(), 10));
        ko.utils.registerEventHandler(element, 'change', () => {
            let value: moment.Moment | undefined = moment(element.value, 'HH:mm');
            if (!value.isValid()) {
                value = undefined;
            }
            valueAccessor()(value);
        });
    },
    update: (element: any, valueAccessor: any) => {
        const picker = $(element).data('dtp');
        const value = ko.unwrap(valueAccessor()) || '';
        const momentValue = moment(value);
        if (momentValue.isValid()) {
            picker.setDate(momentValue.format('HH:mm'));
        } else {
            picker.clear();
        }
    }
};

ko.bindingHandlers.monthPicker = {
    init: (element: any, valueAccessor: any, allBindingsAccessor: any) => {
        const options = {
            ...defaultMonthPickerOptions,
            ...allBindingsAccessor().monthPickerOptions
        };

        const picker = flatpickr(element, options);
        const $el = $(element);
        $el.data('dtp', picker);

        ko.utils.registerEventHandler(element, 'focus', () => setTimeout(() => element.select(), 10));
        ko.utils.registerEventHandler(element, 'change', () => {
            let value: moment.Moment | undefined = moment(element.value, 'MM-YYYY');
            if (!value.isValid()) {
                value = undefined;
            }
            valueAccessor()(value);
        });
    },
    update: (element: any, valueAccessor: any) => {
        const picker = $(element).data('dtp');
        const value = ko.unwrap(valueAccessor()) || '';
        const momentValue = moment(value);
        if (momentValue.isValid()) {
            picker.setDate(momentValue.format('MM-YYYY'));
        } else {
            picker.clear();
        }
    }
};

ko.bindingHandlers.dateInputMask = {
    init: function (element: any, valueAccessor: any) {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', function () {
            if (element.inputmask.isComplete()) {
                let time = moment(element.value, 'DD-MM-YYYY HH:mm');
                valueAccessor()(time);
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('dd-mm-yyyy', {
            placeholder: 'dd/mm/jjjj',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: function (element: any, valueAccessor: any) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = moment(value).format('DD-MM-YYYY HH:mm');
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('dateInputMask');

(ko.extenders as any).errorHandler = (
    target: KnockoutObservable<any>,
    options: {
        source: KnockoutObservableArray<CustomError>, id: string
    }) => {
    target.errors = ko.observableArray<string>([]);
    target.error = ko.pureComputed<string>(() => {
        return target.errors().length > 0
            ? target.errors()[0]
            : '';
    });
    target.hasError = ko.pureComputed<boolean>(() => {
        return target.errors().length > 0;
    });

    const validate = (errors: CustomError[]) => {
        if (!errors || errors.length === 0 || !options.id) {
            target.errors([]);
        } else {
            const targetErrors = errors
                .filter(e => e.key === options.id)
                .map(e => e.message);
            target.errors(targetErrors);
        }
    };

    validate(options.source());

    options.source.subscribe(validate);

    return target;
};

ko.bindingHandlers.timeInputMaskHoursMinutes = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                valueAccessor()(element.value);
            } else {
                valueAccessor()(undefined);
            }
        });

        Inputmask('hh:mm', {
            placeholder: 'hh:mm',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        const value = ko.utils.unwrapObservable(valueAccessor());
        const text = value != null
            ? moment.duration(value, 'ms').format("HH:mm", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeInputMaskHoursMinutes');

ko.bindingHandlers.timeInputMaskHoursMinutesNoPlaceholder = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                valueAccessor()(element.value);
            } else {
                valueAccessor()(undefined);
            }
        });

        Inputmask('hh:mm', {
            placeholder: '-',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        const value = ko.utils.unwrapObservable(valueAccessor());
        const text = value != null
            ? moment.duration(value, 'ms').format("HH:mm", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeInputMaskHoursMinutesNoPlaceholder');

ko.bindingHandlers.timeMsInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let duration = moment.duration(element.value);
                valueAccessor()(duration.asMilliseconds());
            } else {
                valueAccessor()(null);
            }
        });

        Inputmask('hh:mm:ss', {
            placeholder: "uu:mm'ss",
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = value != null
            ? moment.duration(value).format("HH:mm'ss", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeMsInputMask');

ko.bindingHandlers.timeMsDaysInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let duration = moment.duration(element.value);
                valueAccessor()(duration.asMilliseconds());
            } else {
                valueAccessor()(null);
            }
        });
    },
    update: (element: any, valueAccessor: any) => {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = value != null
            ? moment.duration(value).format('HH:mm', { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('timeMsInputMask');

ko.bindingHandlers.durationInputMaskHoursMinutes = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focus', () => setTimeout(() => element.select(), 10));
        $(element).on('focusout change', () => {
            const durationValue = element.value ? moment.duration(element.value) : null;
            valueAccessor()(durationValue);
        });

        Inputmask({
            regex: '^[0-9]{1,3}:[0-5][0-9]$',
            placeholder: '0',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        const value = ko.unwrap(valueAccessor());
        const text = value != null
            ? moment.duration(value).format("hh:mm", { trim: false })
            : element.value;
        element.value = text;
    }
};

(<any>ko).validation.makeBindingHandlerValidatable('durationInputMaskHoursMinutes');

/**
 * Simply call tippy on the given element, setting the tooltip text to the value set in the data-bind.
 * By default, empty tooltip are ignored. To allow an empty tooltip, set the additional bind 'allowEmptyText' to true.
 */
ko.bindingHandlers.tooltip = {
    init: (element: any, valueAccessor: any): void => {
        const text = ko.unwrap(valueAccessor());

        tippy(element, {
            placement: 'bottom',
            followCursor: 'horizontal',
            content: text,
            maxWidth: 350,
            allowHTML: true
        });
    },
    update: (element: any, valueAccessor: any): void => {
        const text = ko.unwrap(valueAccessor());

        if (element._tippy) {
            element._tippy.setContent(text);
        }
    }
};

ko.bindingHandlers.hours = {
    update: (element: any, valueAccessor: any, allBindingsAccessor, viewModel, bindingContext) => {
        const value = ko.unwrap(valueAccessor());

        let hours = value;
        let format = 'HH:mm';
        let emptyPlaceholder = '';

        if (!isDuration(value)) {
            hours = ko.unwrap(value.value);
            format = value.format;
            emptyPlaceholder = value.empty;
        }

        const newValueAccessor = () => formatHours(hours, format, emptyPlaceholder);

        if (ko.bindingHandlers.text.update)
            ko.bindingHandlers.text.update(element, newValueAccessor, allBindingsAccessor, viewModel, bindingContext);
    }
};

ko.bindingHandlers.sortable = {
    getProperty: function (o: any, s: any) {
        // copied from http://stackoverflow.com/questions/6491463/accessing-nested-javascript-objects-with-string-key
        s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
        s = s.replace(/^\./, '');           // strip a leading dot
        var a = s.split('.');
        while (a.length) {
            var n = a.shift();
            if (n in o) {
                o = o[n];
            } else {
                return;
            }
        }
        return o;
    },

    compare: function (left: any, right: any) {
        if (typeof left === 'string' || typeof right === 'string') {
            return left ? left.localeCompare(right) : 1;
        }
        if (left > right)
            return 1;

        return left < right ? -1 : 0;
    },

    sort: function (viewModel: any, collectionName: any, field: any) {
        const collection = ko.unwrap(viewModel[collectionName]);
        //make sure we sort only once and not for every binding set on table header
        if (collection.orderField() == field) {
            const sortedCollection = collection.sort(function (left: any, right: any) {
                var left_field = ko.bindingHandlers.sortable.getProperty(left, field);
                var right_field = ko.bindingHandlers.sortable.getProperty(right, field);
                var left_val = (typeof left_field === 'function') ? left_field() : left_field;
                var right_val = (typeof right_field === 'function') ? right_field() : right_field;
                if (collection.orderDirection() == "desc") {
                    return ko.bindingHandlers.sortable.compare(right_val, left_val);
                } else {
                    return ko.bindingHandlers.sortable.compare(left_val, right_val);
                }
            });

            if (ko.isWriteableObservable(viewModel[collectionName])) {
                viewModel[collectionName](sortedCollection);
            }
        }
    },

    init: function (element, valueAccessor, _allBindingsAccessor, viewModel, _bindingContext) {
        //get provided options
        var collectionName = valueAccessor().collection;
        var collection = ko.unwrap(viewModel[collectionName]);
        var field = valueAccessor().field;

        //add a few observables to ViewModel to track order field and direction
        if (collection.orderField == undefined) {
            collection.orderField = ko.observable();
        }
        if (collection.orderDirection == undefined) {
            collection.orderDirection = ko.observable("asc");
        }

        var defaultField = valueAccessor().defaultField;
        var defaultDirection = valueAccessor().defaultDirection || "asc";
        if (defaultField) {
            collection.orderField(field);
            collection.orderDirection(defaultDirection);
            ko.bindingHandlers.sortable.sort(viewModel, collectionName, field);
        }

        //set order observables on table header click
        $(element).click(function (e) {
            e.preventDefault();

            //flip sort direction if current sort field is clicked again
            if (collection.orderField() == field) {
                if (collection.orderDirection() == "asc") {
                    collection.orderDirection("desc");
                } else {
                    collection.orderDirection("asc");
                }
            }

            collection.orderField(field);
        });

        //order records when observables changes, so ordering can be changed programmatically
        collection.orderField.subscribe(function () {
            ko.bindingHandlers.sortable.sort(viewModel, collectionName, field);
        });
        collection.orderDirection.subscribe(function () {
            ko.bindingHandlers.sortable.sort(viewModel, collectionName, field);
        });
    },

    update: function (element, valueAccessor, allBindingsAccessor, viewModel, bindingContext) {
        //get provided options
        var collectionName = valueAccessor().collection;
        var collection = ko.unwrap(viewModel[collectionName]);
        var field = valueAccessor().field;
        var isOrderedByThisField = collection.orderField() == field;

        //apply css binding programmatically
        if (ko.bindingHandlers.css && ko.bindingHandlers.css.update) {
            ko.bindingHandlers.css.update(
                element,
                function () {
                    return {
                        sorted: isOrderedByThisField,
                        asc: isOrderedByThisField && collection.orderDirection() == "asc",
                        desc: isOrderedByThisField && collection.orderDirection() == "desc"
                    };
                },
                allBindingsAccessor,
                viewModel,
                bindingContext
            );
        }
    }
};

ko.bindingHandlers.integerInputMask = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            if (element.inputmask.isComplete()) {
                let number = numeral(element.value).value();
                valueAccessor()(number);
            } else {
                valueAccessor()(null);
            }
        });

        element.addEventListener('focusin', () => {
            if (element.createTextRange) {
                //IE fallback
                const textRange = element.createTextRange();
                textRange.move('character', 0);
                textRange.select();
            } else if (element.setSelectionRange) {
                //Browser standard
                element.setSelectionRange(0, 0);
            }
        });

        Inputmask('integer', {
            rightAlign: false,
            autoGroup: true,
        }).mask(element);
    },
    update: function (element, valueAccessor) {
        let value = ko.utils.unwrapObservable(valueAccessor());
        let text = numeral(value).format('#0');
        element.value = text;
    }
};

ko.bindingHandlers.timeOfDay = {
    init: (element: any, valueAccessor: any) => {
        $(element).on('focusout change', () => {
            const durationValue = moment.duration(element.value.replace(/-/g, '0'));

            if (durationValue.isValid() && durationValue.asMilliseconds() > 0) {
                const currentMoment = ko.unwrap(valueAccessor()) || moment();

                valueAccessor()(currentMoment
                    .hours(durationValue.hours())
                    .minutes(durationValue.minutes())
                    .seconds(0));
            } else {
                valueAccessor()(undefined);
            }
        });

        Inputmask('hh:mm', {
            placeholder: '-',
            clearMaskOnLostFocus: false
        }).mask(element);
    },
    update: (element: any, valueAccessor: any) => {
        const value = ko.unwrap(valueAccessor());
        if (value && value.isValid()) {
            element.value = value.format('HH:mm');
        } else {
            element.value = null;
        }

        // const text = value
        //     ? moment.duration(value).format("hh:mm", { trim: false })
        //     : element.value;
        // element.value = text;
    }
};
